import { Component, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RippleModule } from 'primeng/ripple';
import { InlineInputControlComponent } from '../../components/form/inline-input-control/inline-input-control.component';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ProductDetailsHeaderComponent } from '../../components/product/product-details-header/product-details-header.component';
import {
  Asset,
  Discount,
  Manufacturer,
  Mutation,
  MutationAddProductInquiryArgs,
  Product,
  ProductVariant,
  SearchResult,
  SubmitProductInquiryInput,
  SubmitProductInquiryResult,
} from '../../../graphql/generated/generated-types';
import { ProductImageWrapperComponent } from '../../components/product/product-image-wrapper/product-image-wrapper.component';
import { AssetPreviewPipe } from '../../pipes/asset-preview.pipe';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { Observable, map, take } from 'rxjs';
import { DataService } from '../../../core/providers/data/data.service';
import { MessageService } from 'primeng/api';
import { ToastSeverity } from '../../notification/notification.component';
import { HttpErrorResponse } from '@angular/common/http';
import { REQUEST_ITEM } from './request-item.graphql';

export enum RequestItemFields {
  ProductVariantId = 'productVariantId',
  Quantity = 'quantity',
  Phone = 'phone',
  Email = 'email',
  Comments = 'comments',
}

export interface IRequestItemDialogData {
  productVariantId: any;
  name: string;
  slug: string;
  manufacturer?: Manufacturer;
  sku?: string;
  discounts?: Discount[];
  stockLevel?: number;
  quantity?: boolean;
  featuredAsset?: Asset;
}
@Component({
  selector: 'app-request-part-dialog',
  standalone: true,
  imports: [
    ButtonModule,
    RippleModule,
    InlineInputControlComponent,
    ReactiveFormsModule,
    ProductDetailsHeaderComponent,
    ProductImageWrapperComponent,
    AssetPreviewPipe,
    InputTextareaModule,
  ],
  templateUrl: './request-part-dialog.component.html',
  styleUrl: './request-part-dialog.component.scss',
})
export class RequestPartDialogComponent implements OnInit {
  public dialogData: IRequestItemDialogData;

  public loading: boolean = false;
  public RequestItemFields = RequestItemFields;
  public form: FormGroup = new FormGroup({
    [RequestItemFields.ProductVariantId]: new FormControl(undefined, [
      Validators.required,
    ]),
    [RequestItemFields.Quantity]: new FormControl(1, [Validators.required]),
    [RequestItemFields.Phone]: new FormControl('', [Validators.required]),
    [RequestItemFields.Email]: new FormControl('', [
      Validators.email,
      Validators.required,
    ]),
    [RequestItemFields.Comments]: new FormControl(''),
  });

  constructor(
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private dataService: DataService,
    private messageService: MessageService
  ) {
    this.dialogData = this.config.data;
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  ngOnInit(): void {
    this.form
      .get(RequestItemFields.ProductVariantId)
      ?.setValue(this.dialogData.productVariantId);
  }

  public submitRequestItem() {
    this.loading = true;
    const exportedForm: SubmitProductInquiryInput = this.form.getRawValue();
    exportedForm.quantity = Number(exportedForm.quantity);
    if (exportedForm && exportedForm?.productVariantId) {
      this.sendRequestItemForm(exportedForm)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.messageService.add({
              severity: ToastSeverity.Success,
              key: 'notifications',
              summary: 'Item request was sent',
              detail: 'We will contact you as soon as possible.',
            });
          },
          error: (error: HttpErrorResponse) => {
            this.messageService.add({
              severity: ToastSeverity.Error,
              key: 'notifications',
              summary: 'Error',
              detail: `${error.message}`,
            });
            this.loading = false;
          },
          complete: () => {
            this.loading = false;
            this.dialogRef.close();
          },
        });
    }
  }

  public sendRequestItemForm(
    form: SubmitProductInquiryInput
  ): Observable<SubmitProductInquiryResult> {
    return this.dataService
      .mutate<Mutation, MutationAddProductInquiryArgs>(REQUEST_ITEM, {
        input: form,
      })
      .pipe(
        map((data) => data.addProductInquiry as SubmitProductInquiryResult)
      );
  }
}
